import React, { useEffect, useState } from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Box, Button, FormControl, Popover, TextField } from '@material-ui/core';
import DayjsUtils from '@date-io/dayjs';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import Grid from '@material-ui/core/Grid';
import { modifyDocumentsByBatch } from '../../models/api/documents';
import { INumberedDocumentView } from '../../api-client/autogenerated';
import { useDispatch, useSelector } from 'react-redux';
import { reloadDocuments } from '../../features/documents/actions';
import { TooltipIfDisabledComponent } from '../custom-components/CustomButtons';
import { getDocumentsState } from '../../features/documents/selectors';
import CircularLoader from '../loader/CircularLoader';

type EditDrawingsPackageDialogProps = {
  dialogOpen: boolean;
  handleClose: () => void;
  anchor: HTMLElement | null;
  selectedDocuments: INumberedDocumentView[];
};

const useStyles = makeStyles(
  createStyles({
    popover: {
      '.rootElement > *': {
        maxHeight: '5000px',
      },
    },
    section: {
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'left',
      minWidth: '270px',
    },
    button: {
      width: 100,
      height: 36,
    },
  }),
);

const hasSameSheetNumberAndPackage = (a: INumberedDocumentView, b: INumberedDocumentView) => {
  return (
    a.id !== b.id &&
    a.sheetNumber === b.sheetNumber &&
    (a.simplePackage === b.simplePackage || (!a.simplePackage && !b.simplePackage))
  );
};

export default function EditDrawingsPackageDialog(props: EditDrawingsPackageDialogProps) {
  const classes = useStyles();
  const { dialogOpen, handleClose, anchor, selectedDocuments } = props;

  const documents = useSelector(getDocumentsState);
  const dispatch = useDispatch();

  const [simplePackage, setSimplePackage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const pendingDocuments = selectedDocuments.map((d) => ({ ...d, simplePackage }));
    if (
      pendingDocuments.length > 1 &&
      pendingDocuments.some(
        (pd1) =>
          documents.some((d) => hasSameSheetNumberAndPackage(pd1, d)) ||
          pendingDocuments.some((pd2) => hasSameSheetNumberAndPackage(pd1, pd2)),
      )
    ) {
      setError(true);
    } else {
      setError(false);
    }
  }, [simplePackage, selectedDocuments, documents]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      await modifyDocumentsByBatch(
        selectedDocuments.map((d) => d.id),
        {
          patch: {
            simplePackage: simplePackage,
          },
        },
      );
      handleClose();
      dispatch(reloadDocuments());
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSimplePackage('');
    handleClose();
  };

  useEffect(() => {
    setSimplePackage('');
  }, []);

  return (
    <MuiPickersUtilsProvider utils={DayjsUtils}>
      <Popover
        className={classes.popover}
        open={dialogOpen}
        onClose={handleClose}
        anchorEl={anchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box className={classes.section}>
          <Grid container direction="column">
            <FormControl fullWidth variant="outlined" style={{ marginBottom: 12 }}>
              <TextField
                variant="outlined"
                label="Package"
                value={simplePackage}
                onChange={(e) => setSimplePackage(e.target.value)}
              />
            </FormControl>
            <Grid container style={{ justifyContent: 'space-between' }}>
              <Button onClick={handleCancel} variant="outlined" className={classes.button}>
                Cancel
              </Button>
              {isLoading ? (
                <CircularLoader size={30} style={{ marginRight: 36 }} />
              ) : (
                <TooltipIfDisabledComponent
                  title="Multiple Drawings documents cannot have the same Sheet Number and Package name"
                  disabled={error}
                  spanStyle={{ width: 'auto' }}
                >
                  <Button
                    color="primary"
                    className={classes.button}
                    disabled={error}
                    onClick={handleSubmit}
                    variant="contained"
                  >
                    Update
                  </Button>
                </TooltipIfDisabledComponent>
              )}
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </MuiPickersUtilsProvider>
  );
}
